import * as dockerNames from "docker-names";

import { titleCase } from "../util/strings";
import { sample } from "../lib/util";
import { defaultCloud } from "./constants";

export type CloudId = string;
export type RegionId = string;
export type CellId = string;

export type CellManifest = {
  [cloudId: CloudId]: {
    [regionId: RegionId]: CellId[];
  };
};

export function generateDisplayName() {
  return generateDisplayNameFromParts(dockerNames.left, dockerNames.right);
}

export function generateDisplayNameFromParts(left: string[], right: string[]) {
  return `${titleCase(sample(left))} ${titleCase(sample(right))}`;
}

const patterns = {
  invalidAccountNameChars: /[^\sa-z0-9-]+/g,
  spaces: /\s+/g,
};

export const byocVersions = {
  "v1.18.1": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.18.0": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.17.5": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.17.4": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.17.3": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.17.2": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.17.1": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.17.0": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.16.3": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.16.2": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.16.1": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.16.0": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.15.1": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
  "v1.15.0": [
    { name: "Data Plane Helm Chart", file: "decodable-data-plane.tgz" },
    {
      name: "Flink Kubernetes Operator Helm Chart",
      file: "flink-kubernetes-operator.tgz",
    },
    { name: "Reference Terraform", file: "terraform.zip" },
  ],
};

export function accountNameFromDisplayName(displayName: string) {
  return displayName
    .toLocaleLowerCase()
    .replace(patterns.invalidAccountNameChars, "")
    .trim()
    .replace(patterns.spaces, "-")
    .slice(0, 50);
}

export function selectDefaultRegion(cells: CellManifest): RegionId {
  // Find the regions with a non-empty list of available cells.
  const awsRegions = Object.keys(cells?.[defaultCloud] ?? {}).filter(
    (r) => cells[defaultCloud][r].length > 0
  );

  let someUsWestRegion: string = null;
  let someUsEastRegion: string = null;
  let someUsRegion: string = null;

  // Default region preference is us-west-2 > any us-west > any us-east > any us > any region
  for (const region of awsRegions) {
    if (region === "us-west-2") {
      return region;
    }

    if (!someUsWestRegion && region.startsWith("us-west")) {
      someUsWestRegion = region;
    }

    if (!someUsEastRegion && region.startsWith("us-east")) {
      someUsEastRegion = region;
    }

    if (!someUsRegion && region.startsWith("us-")) {
      someUsRegion = region;
    }
  }

  return someUsWestRegion ?? someUsEastRegion ?? someUsRegion ?? awsRegions[0];
}
